import { z } from "zod/v4";
import { StoreField, useStoreFields } from "../composables/useStoreFields";
import { useDistributionContentMutation } from "@/features/experience-raw/queries/distribution-content-mutation";
import { defineStore, acceptHMRUpdate } from "pinia";
import { useCurrentDistributionContentQuery } from "../composables/useCurrentDistributionContentQuery";

export type DistributionContentValues = {
  curation_level?: string;
  priority?: number;
};

export const curationLevelField: StoreField = {
  key: "curation_level",
  isRequired: false,
  schema: z.string().min(1),
};

export const priorityField: StoreField = {
  key: "priority",
  isRequired: false,
  schema: z.number().int().min(0),
};

export const usePriorityAndCurationStore = defineStore("usePriorityAndCurationStore", () => {
  const distributionContentQuery = useCurrentDistributionContentQuery();
  const distributionContentMutation = useDistributionContentMutation();

  const experienceId = computed(() => distributionContentQuery.data.value?.id);
  const productType = computed(() => distributionContentQuery.data.value?.experience_source);

  const isSaving = computed(() => distributionContentMutation.isPending.value);
  const canSave = computed(() => !isSaving.value && formFields.isValid.value && formFields.hasChanges.value);

  const formFields = useStoreFields(
    () => {
      const fields = [curationLevelField, priorityField];
      return fields;
    },
    {
      initialValues: distributionContentQuery.data,
    }
  );
  return {
    productType,
    save,
    isSaving,
    canSave,
    enabled: true,
    ...formFields,
    reset,
    $reset: reset,
  };

  async function save() {
    if (!formFields.hasChanges.value) {
      return;
    }

    if (!experienceId.value) {
      throw new Error("experienceId is required");
    }

    const promises: Promise<unknown>[] = [
      distributionContentMutation.mutateAsync({
        id: experienceId.value,
        ...formFields.updatedValues.value,
      }),
    ];

    await Promise.all(promises);
  }

  function reset() {
    Object.assign(formFields.values, distributionContentQuery.data.value);
  }
});

// HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePriorityAndCurationStore, import.meta.hot));
}
